import { ClientSideDataGridModel } from "src/components/common/dataGrid/gridModel/ClientSideDataGridModel.tsx";
import { AavoDataGrid } from "src/components/common/dataGrid/AavoDataGrid.tsx";
import { dateColumn, enumColumn, integerColumn, textColumn } from "src/components/common/dataGrid/columns.tsx";
import i18n from "i18next";
import { ProjectActivityView } from "src/api/generated/erp/db/types/tables/projectActivityView.ts";
import { getActivityStateLabels } from "src/api/generated/erp/db/types/enums/activityState.ts";
import { DocumentsOfObjectButton } from "src/components/views/documents/objectDocuments/DocumentsOfObjectButton.tsx";
import { nullableAavoObjectRef } from "src/utils/aavoObjectRefUtils.ts";
import { SalvosDeliveryTransportProjectActivitiesApi } from "src/api/generated/tenants/salvos/erp/delivery/salvosDeliveryTransportProjectActivitiesApi.ts";
import { AavoButton } from "src/components/common/buttons/AavoButton.tsx";
import { faPaperclip, faTasks } from "@fortawesome/pro-regular-svg-icons";
import { useGenericDialog } from "src/components/common/dialogs/GenericDialogContext.ts";
import { SingleSourceTasksView } from "src/components/views/tasks/pages/SingleSourceTasksView.tsx";
import { DocumentsOfSingleObjectView } from "src/components/views/documents/objectDocuments/DocumentsOfSingleObjectView.tsx";
import { DocumentsOfMultipleObjectsView } from "src/components/views/documents/objectDocuments/DocumentsOfMultipleObjectsView.tsx";
import { TaskQueryApi } from "src/api/generated/tasks/api/taskQueryApi.ts";
import { useParameterizedAsyncData } from "src/utils/async/parameterizedAsyncData.ts";
import { emptyDataModelRequest } from "src/utils/serverSideDataModelRequestUtils.ts";
import { AsyncRender } from "src/components/common/async/AsyncRender.tsx";
import { CenteredTypography } from "src/components/common/CenteredTypography.tsx";
import { expectNonFileResult } from "src/components/common/dataGrid/gridModel/serverSideDataModelUtils.ts";
import { useMediaQueryBreakpointDown } from "src/utils/breakpointUtils.ts";
import { defineAgentUiContextKey } from "src/components/views/ai/assistant/uiContext/agentUiContextKey.ts";

export interface SalvosTransportProjectActivitiesDataGridProps {
	deliveryTransportId: number;
}

export const SalvosTransportProjectActivitiesDataGrid = ({
	deliveryTransportId,
}: SalvosTransportProjectActivitiesDataGridProps) => {
	const { openDialog } = useGenericDialog();
	const isMobile = useMediaQueryBreakpointDown("sm");

	return (
		<ClientSideDataGridModel
			fetchData={() =>
				SalvosDeliveryTransportProjectActivitiesApi.getTransportProjectActivities({
					deliveryTransportId,
				})
			}
			initialParams={{}}
			gridId={"F8B2C4A1E7D6539B"}
			getRows={(response) => response}
			getRowId={(row) => row.activityId}
			render={({ dataGridProps, onlySelectedRow }) => (
				<AavoDataGrid<ProjectActivityView>
					columns={[
						textColumn({
							field: "projectDescription",
							headerName: i18n.t("project"),
							width: 200,
							mobile: "header",
						}),
						textColumn({
							field: "activityName",
							headerName: i18n.t("name"),
							width: 200,
							mobile: "cell",
						}),
						enumColumn({
							field: "activityState",
							headerName: i18n.t("state"),
							enumLabels: getActivityStateLabels(),
							width: 120,
							mobile: "cell",
						}),
						dateColumn({
							field: "plannedEndDate",
							headerName: i18n.t("planned_end"),
							width: 120,
							mobile: "cell",
						}),
						textColumn({
							field: "note",
							headerName: i18n.t("note"),
							width: 300,
							mobile: "cell",
						}),
						textColumn({
							field: "responsiblePersonName",
							headerName: i18n.t("responsible_person"),
							width: 150,
						}),
						integerColumn({
							field: "activityId",
							headerName: i18n.t("id"),
							width: 75,
							mobile: "details",
						}),
					]}
					listViewOnMobile={{
						detailsView: ({ row }) => ({
							type: "tabs",
							items: [
								{
									title: i18n.t("tasks"),
									icon: faTasks,
									content: (
										<SingleSourceTasksView
											taskSourceRef={{
												sourceType: "PROJECT_ACTIVITY",
												sourceId: row.activityId,
											}}
										/>
									),
								},
								{
									title: i18n.t("activity_documents"),
									icon: faPaperclip,
									content: (
										<DocumentsOfSingleObjectView
											objectRef={{
												objectType: "PROJECT_ACTIVITY",
												objectId: row.activityId,
											}}
											editable={false}
											hidePreview
										/>
									),
								},
								{
									title: i18n.t("task_documents"),
									icon: faPaperclip,
									content: <ActivityTaskDocumentsView activityId={row.activityId} />,
								},
							],
						}),
					}}
					actionBarComponents={
						<>
							<DocumentsOfObjectButton
								objectRef={nullableAavoObjectRef("PROJECT_ACTIVITY", onlySelectedRow?.activityId)}
								editable={false}
							/>
							{!isMobile && (
								<AavoButton
									icon={faTasks}
									label={i18n.t("tasks")}
									disabled={onlySelectedRow == null}
									onClick={() => {
										if (onlySelectedRow == null) return;

										openDialog({
											title: i18n.t("tasks"),
											agentContext: {
												key: defineAgentUiContextKey(
													"tenant.salvos.transportActivityTasks.dialog",
												),
												data: { activityId: onlySelectedRow.activityId },
												pathSegments: [`${i18n.t("activity")} ${onlySelectedRow.activityId}`],
											},
											content: (
												<SingleSourceTasksView
													taskSourceRef={{
														sourceType: "PROJECT_ACTIVITY",
														sourceId: onlySelectedRow.activityId,
													}}
												/>
											),
										});
									}}
								/>
							)}
							{!isMobile && (
								<AavoButton
									icon={faPaperclip}
									label={i18n.t("task_documents")}
									disabled={onlySelectedRow == null}
									onClick={() => {
										if (onlySelectedRow == null) return;

										openDialog({
											title: i18n.t("task_documents"),
											agentContext: {
												key: defineAgentUiContextKey(
													"tenant.salvos.transportActivityTaskDocuments.dialog",
												),
												data: { activityId: onlySelectedRow.activityId },
												pathSegments: [i18n.t("task_documents")],
											},
											content: (
												<ActivityTaskDocumentsView activityId={onlySelectedRow.activityId} />
											),
										});
									}}
								/>
							)}
						</>
					}
					agentContext={{
						key: defineAgentUiContextKey("tenant.salvos.transportProjectActivities.grid"),
						data: { deliveryTransportId },
						rowFields: ["activityId", "projectId", "activityName", "projectDescription"],
						selectedRowPathSegment: (row) => `${i18n.t("activity")} ${row.activityId}`,
						selectedRowsPathSegment: (count) => i18n.t("rows_selected_count", { count }),
					}}
					{...dataGridProps}
				/>
			)}
		/>
	);
};

interface ActivityTaskDocumentsViewProps {
	activityId: number;
}

const ActivityTaskDocumentsView = ({ activityId }: ActivityTaskDocumentsViewProps) => {
	const { dataAsync } = useParameterizedAsyncData({
		initialParams: { activityId },
		fetchData: async ({ activityId }) => {
			const taskResult = await TaskQueryApi.queryTasks({
				sources: [{ sourceType: "PROJECT_ACTIVITY", sourceId: activityId }],
				searchQuery: "",
				dataModelRequest: emptyDataModelRequest,
			});
			const taskRows = expectNonFileResult(taskResult).rows;
			const taskIds: number[] = [...new Set(taskRows.map((task) => task.taskId))];

			return taskIds.map((taskId) => ({
				objectType: "TASK" as const,
				objectId: taskId,
			}));
		},
	});

	return (
		<AsyncRender
			asyncData={dataAsync}
			content={(taskObjectRefs) =>
				taskObjectRefs.length === 0 ?
					<CenteredTypography>{i18n.t("no_data")}</CenteredTypography>
				:	<DocumentsOfMultipleObjectsView
						objectRefs={taskObjectRefs}
						editable={false}
						hidePreview
						viewId={`salvos_activity_task_documents_${activityId}`}
					/>
			}
		/>
	);
};
